<template>
  <PageLayout>
    <template #header>
      <div></div>
    </template>

    <div class="pb-10">
      <VBusHelper :emitOnMounted="$constants.EVENTS.REWARDS.FETCH_PROFILE" />
      <VBusHelper
        :emitOnMounted="$constants.EVENTS.ORDERS.LIST"
        :dataOnMounted="'delivered'"
      />
      <div class="mobile-status-padding"></div>

      <!-- Header -->
      <div class="pt-6 px-6 flex items-center justify-center">
        <h1 class="text-3xl font-bold title-color">Mon compte</h1>
      </div>

      <!-- Profile -->
      <div class="mt-8 flex flex-col items-center text-center px-6">
        <div class="relative w-32 h-32 group">
          <ImgComponent
            :src="userProfilePicture || userEmail"
            :avatar="!userProfilePicture"
            class="w-full h-full rounded-full bg-slate-100 object-cover shadow-md"
          />

          <!-- Hover overlay (desktop) -->
          <div
            class="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none"
          >
            <svg
              class="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>

          <!-- Change photo indicator badge (persistent) -->
          <label
            for="avatar-upload"
            class="absolute -bottom-1 -right-1 w-10 h-10 bg-primary rounded-full border-4 border-white flex items-center justify-center shadow-lg cursor-pointer transform hover:scale-110 active:scale-95 transition-all z-10"
          >
            <input
              id="avatar-upload"
              type="file"
              class="hidden"
              accept="image/*"
              @change="handleAvatarChange"
            />
            <svg
              class="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2.5"
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </label>
        </div>

        <h2 class="mt-4 text-2xl font-bold title-color truncate max-w-full">
          {{ userFirstName }} {{ userLastName }}
        </h2>

        <p class="text-gray-500">
          {{ userEmail }}
        </p>
      </div>

      <!-- Stats -->
      <div class="mt-8 px-6 grid grid-cols-2 gap-4">
        <!-- Level -->
        <div class="flex items-center gap-4 p-4 rounded-3xl liquidGlassCard">
          <div
            class="relative w-14 h-14 min-w-14 flex items-center justify-center"
          >
            <ImgComponent src="/images/badge/badge.png" class="w-full h-full" />
            <div class="absolute text-3xl font-bold text-white">
              {{ userLevel || '-' }}
            </div>
          </div>

          <div>
            <p class="text-primary font-bold text-lg">
              {{ userLevelName || '---' }}
            </p>
            <p class="text-gray-600 text-sm">{{ userTotalXp ?? '---' }} XP</p>
          </div>
        </div>

        <!-- Orders -->
        <div class="flex items-center gap-4 p-4 rounded-3xl liquidGlassCard">
          <div
            class="w-12 h-12 rounded-full bg-white flex items-center justify-center"
          >
            <ImgComponent src="/images/all/parcel.svg" class="w-6 h-6" />
          </div>

          <div>
            <p class="text-primary font-bold text-lg">Commandes</p>
            <p class="text-gray-600 text-sm">
              {{ totalDeliveredOrders ?? '-' }} livrées
            </p>
          </div>
        </div>
      </div>
      <!-- Badges -->
      <div v-if="badges && badges.length > 0" class="mx-6 mt-6">
        <h3 class="text-lg font-bold text-gray-800 mb-3">Mes Badges</h3>
        <div
          class="flex overflow-x-auto gap-4 pb-2"
          style="scrollbar-width: none; -ms-overflow-style: none"
        >
          <div
            v-for="(badge, index) in badges"
            :key="index"
            class="flex-shrink-0 w-20 h-20 rounded-full bg-white shadow-md border border-gray-100 flex items-center justify-center p-2"
          >
            <ImgComponent :src="badge" class="w-full h-full object-contain" />
          </div>
        </div>
      </div>
      <!-- Badges Empty State -->
      <div v-else class="mx-6 mt-6">
        <h3 class="text-lg font-bold text-gray-800 mb-3">Mes Badges</h3>
        <div
          class="bg-white rounded-3xl p-6 text-center shadow-sm border border-gray-100 cursor-pointer ripple"
          @click="$router.push('/rewards')"
        >
          <div class="w-16 h-16 mx-auto bg-orange-50 rounded-full flex items-center justify-center mb-3">
            <span class="text-3xl">🏅</span>
          </div>
          <p class="text-gray-800 font-medium mb-1">Aucun badge pour le moment</p>
          <p class="text-primary text-sm font-bold">Découvrez les défis pour en gagner &rarr;</p>
        </div>
      </div>

      <!-- Rapidoo Connect Redirection -->
      <div
        class="flex items-center justify-between p-4 mt-8 bg-gradient-to-r from-primary to-orange-500 rounded-3xl text-white shadow-lg mx-6 ripple"
        @click="$router.push('/rewards')"
      >
        <div class="flex items-center gap-4">
          <div
            class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center"
          >
            <ImgComponent src="/images/badge/badge.png" class="w-8 h-8" />
          </div>
          <div>
            <p class="font-bold text-lg">Rapidoo Connect</p>
            <p class="text-sm opacity-90">Gagnez des points & récompenses</p>
          </div>
        </div>
        <ImgComponent
          src="/images/all/right-arrow.svg"
          class="w-5 h-5 brightness-0 invert"
        />
      </div>

      <!-- Referral Link Card -->
      <div
        class="flex flex-col p-4 mt-4 bg-orange-50 border border-orange-200 rounded-3xl mx-6 shadow-sm"
        v-if="referralCode"
      >
        <div class="flex items-center gap-4 mb-3">
          <div
            class="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center"
          >
            <span class="text-xl">🎁</span>
          </div>
          <div class="flex-1">
            <p class="font-bold text-gray-800">Parrainez vos amis</p>
            <p class="text-xs text-gray-600">
              Partagez votre lien et gagnez +45 XP
            </p>
          </div>
        </div>

        <div
          class="flex items-center gap-2 bg-white p-2 rounded-2xl border border-gray-100"
        >
          <p class="flex-1 text-xs text-gray-500 truncate select-all">
            {{ referralLink }}
          </p>
          <button
            @click="copyReferralLink"
            class="bg-primary text-white text-xs px-3 py-1.5 rounded-xl font-bold active:scale-95 transition-transform"
          >
            {{ copyButtonText }}
          </button>
        </div>
      </div>

      <!-- Actions -->
      <div class="mt-8 px-6 space-y-4">
        <div
          class="flex items-center gap-4 p-4 rounded-3xl liquidGlassCard ripple"
          @click="openFavorites"
        >
          <div
            class="w-12 h-12 rounded-full bg-white flex items-center justify-center"
          >
            <ImgComponent src="/images/all/heart-primary.svg" class="w-6 h-6" />
          </div>
          <div class="flex-1">
            <p class="font-bold text-gray-800">Vos favoris</p>
            <p class="text-sm text-gray-500">Plats enregistrés</p>
          </div>
          <ImgComponent
            src="/images/all/right-arrow.svg"
            class="w-4 h-4 opacity-60"
          />
        </div>

        <div
          class="flex items-center gap-4 p-4 rounded-3xl liquidGlassCard ripple"
          @click="openAddressBook"
        >
          <div
            class="w-12 h-12 rounded-full bg-white flex items-center justify-center"
          >
            <ImgComponent src="/images/all/location.svg" class="w-6 h-6" />
          </div>
          <div class="flex-1">
            <p class="font-bold text-gray-800">Carnet d'adresses</p>
            <p class="text-sm text-gray-500">Lieux de livraison</p>
          </div>
          <ImgComponent
            src="/images/all/right-arrow.svg"
            class="w-4 h-4 opacity-60"
          />
        </div>

        <div
          class="flex items-center gap-4 p-4 rounded-3xl liquidGlassCard ripple"
          @click="openPaymentMethods"
        >
          <div
            class="w-12 h-12 rounded-full bg-white flex items-center justify-center"
          >
            <ColorImg src="/images/all/card.svg" class="w-6 h-6 bg-primary" />
          </div>
          <div class="flex-1">
            <p class="font-bold text-gray-800">Méthodes de paiement</p>
            <p class="text-sm text-gray-500">Cartes & wallets</p>
          </div>
          <ImgComponent
            src="/images/all/right-arrow.svg"
            class="w-4 h-4 opacity-60"
          />
        </div>

        <div
          class="flex items-center gap-4 p-4 rounded-3xl liquidGlassCard ripple"
          @click="openSupportChat"
        >
          <div
            class="w-12 h-12 rounded-full bg-white flex items-center justify-center"
          >
            <ColorImg src="/images/all/help.svg" class="w-6 h-6 bg-primary" />
          </div>
          <div class="flex-1">
            <p class="font-bold text-gray-800">Aide</p>
            <p class="text-sm text-gray-500">Besoin d'assistance ?</p>
          </div>
          <ImgComponent
            src="/images/all/right-arrow.svg"
            class="w-4 h-4 opacity-60"
          />
        </div>
      </div>

      <!-- Logout -->
      <div class="mt-10 px-6">
        <button
          @click="handleLogout"
          class="w-full flex items-center justify-between px-8 py-4 rounded-3xl font-bold liquidGlassCard"
        >
          Me déconnecter
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
        </button>
      </div>

      <!-- Delete Account -->
      <div class="mt-4 px-6">
        <button
          @click="handleDeleteAccount"
          class="w-full flex items-center justify-between px-8 py-4 rounded-3xl font-bold text-red-500 hover:text-red-600 liquidGlassCard"
        >
          Supprimer mon compte
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      <div class="py-8"></div>
    </div>
  </PageLayout>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import Commons from '@features/shared/libraries/commons';

// Computed properties for user data
const userProfilePicture = computed(() => {
  return window.$stores?.user?.state?.current?.profilePictureUrl || null;
});

const badges = computed(() => {
  return window.$stores?.user?.state?.current?.metadata?.badges || [];
});

const userEmail = computed(() => {
  return window.$stores?.user?.state?.current?.email || '';
});

const userFirstName = computed(() => {
  return window.$stores?.user?.state?.current?.firstName || '';
});

const userLastName = computed(() => {
  return window.$stores?.user?.state?.current?.lastName || '';
});

const userLevel = computed(() => {
  return window.$stores?.rewards?.state?.userLevelData?.level || 1;
});

const userLevelName = computed(() => {
  return window.$stores?.rewards?.state?.levelName || 'Débutant';
});

const userTotalXp = computed(() => {
  return window.$stores?.rewards?.state?.userLevelData?.totalXp || 0;
});

const totalDeliveredOrders = computed(() => {
  // Calculate from orders list if available
  const orders = window.$stores?.orders?.state?.list?.items || [];
  return orders.filter((order) => order.status === 'delivered').length;
});

const referralCode = computed(() => {
  return window.$stores?.user?.state?.current?.referralCode || '';
});

const referralLink = computed(() => {
  return `${constants.APP_URL}/register?ref=${referralCode.value}`;
});

const copyButtonText = ref('Copier');

// Event handlers
const openFavorites = () => {
  window.$router.push('/favorites');
};

const openAddressBook = () => {
  // Use V3 deliverybook modal event
  bus.emit(constants.EVENTS.DELIVERYBOOK.MODAL_OPEN, {
    isFromCart: false
  });
};

const openPaymentMethods = () => {
  // Use V3 wallet modal event
  bus.emit(constants.EVENTS.WALLET.MODAL_OPEN);
};

const openSupportChat = () => {
  // Common chat open event as used in HomePage
  bus.emit('chat.open_with_admin');
};

const handleLogout = () => {
  bus.emit(constants.EVENTS.USER.LOGOUT);
};

const handleDeleteAccount = async () => {
  try {
    const result = await window.customPrompt?.({
      title: 'Supprimer mon compte',
      message:
        'Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.',
      successText: 'Supprimer',
      cancelText: 'Annuler'
    });

    if (result?.accepted) {
      bus.once(constants.EVENTS.USER.UPDATE_SUCCESS, () => {
        bus.emit(constants.EVENTS.USER.LOGOUT);
      });
      bus.emit(constants.EVENTS.USER.UPDATE, {
        metadata: { isDeleted: true }
      });
    }
  } catch (error) {
    console.error('Error deleting account:', error);
  }
};

const copyReferralLink = () => {
  if (!referralLink.value) return;

  Commons.copyToClipboard(
    referralLink.value,
    () => {
      copyButtonText.value = 'Copié !';
      setTimeout(() => {
        copyButtonText.value = 'Copier';
      }, 2000);
    },
    (err) => {
      console.error('Failed to copy referral link:', err);
    }
  );
};

// Load profile data on mount
onMounted(() => {
  bus.emit(constants.EVENTS.PROFILE.LOAD_DATA);
});

const handleAvatarChange = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const img = new Image();
  img.src = URL.createObjectURL(file);

  img.onload = () => {
    const canvas = document.createElement('canvas');
    // Taille fixe idéale pour un avatar (ex: 500x500)
    canvas.width = 500;
    canvas.height = 500;

    // Dessine l'image en l'adaptant à la taille du canvas
    canvas.getContext('2d').drawImage(img, 0, 0, 500, 500);
    URL.revokeObjectURL(img.src); // Libère la mémoire

    // Compression en JPEG à 70% de qualité
    bus.emit(constants.EVENTS.USER.UPDATE, {
      metadata: { avatar: canvas.toDataURL('image/jpeg', 0.7) }
    });
  };
};
</script>

<style scoped>
.title-color {
  color: #1a1a1a;
}

.text-primary {
  color: #f59e0b;
}
</style>
